import { useEffect, useState } from "react";
import { ActionByType } from "../../common/ProfileDocument";
import { TrainingOrder, TrainingOrderStatus } from "../../common/Training";
import { useGetTrainingOrdersByMarinerQuery } from "../../redux/api/trainingOrder";
import convertDateToString from "../../util/ConvertDateToString";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  TableBody,
  Button,
} from "@mui/material";
import { formatDateString } from "../../common/FormatDateString";
import { Edit, Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { selectCurrentUser } from "../../redux/authentication/authSlice";
import { useSelector } from "react-redux";
import { UserGroup } from "../../common/User";

interface ProfileTrainingsProps {
  profileId: number;
  isViewOnly: boolean;
  actionByType: ActionByType;
}

const ProfileTrainings = (props: ProfileTrainingsProps) => {
  const user = useSelector(selectCurrentUser);
  const userGroup = user?.userGroup as UserGroup;

  const [profileTrainingOrders, setProfileTrainingOrders] = useState<
    TrainingOrder[]
  >([]);
  const [showExpiredOrders, setShowExpiredOrders] = useState(true);
  const navigate = useNavigate();
  const redirectToTrainingOrder = (trainingOrderId: number) => {
    navigate(`/trainingOrders/${trainingOrderId}`);
  };

  const {
    data: trainingOrdersFromApi,
    isLoading,
    isFetching,
  } = useGetTrainingOrdersByMarinerQuery(props.profileId);

  const canEditTrainings = () =>
    userGroup === UserGroup.CREWING_SPECIALISTS ||
    userGroup === UserGroup.CREWING_EVAL_SPECIALISTS ||
    userGroup === UserGroup.CREWING_MANAGERS ||
    userGroup === UserGroup.HUMAN_RESOURCES_ADMINS ||
    userGroup === UserGroup.CREWING_EXECS;

  useEffect(() => {
    if (!isFetching && !isLoading) {
      let res: any = JSON.parse(JSON.stringify(trainingOrdersFromApi));
      setProfileTrainingOrders(
        res
          .filter(
            (order: any) =>
              showExpiredOrders || order.status !== TrainingOrderStatus.EXPIRED
          )
          .sort((orderA: any, orderB: any) => {
            return (
              new Date(orderA.expirationDate).getTime() -
              new Date(orderB.expirationDate).getTime()
            );
          })
          .map((order: any) => {
            // convert null values to empty string
            Object.keys(order).forEach(
              (key) => (order[key] = order[key] === null ? "" : order[key])
            );
            // make sure Completed Date and Expiration Date are strings
            if (
              order["completedDate"] !== undefined &&
              order["completedDate"] !== ""
            ) {
              order["completedDate"] = convertDateToString(
                new Date(order["completedDate"])
              );
            }
            if (
              order["expirationDate"] !== undefined &&
              order["expirationDate"] !== ""
            ) {
              order["expirationDate"] = convertDateToString(
                new Date(order["expirationDate"])
              );
            }
            return order as TrainingOrder;
          })
      );
    }
  }, [trainingOrdersFromApi, isFetching, isLoading, showExpiredOrders]);

  return (
    <>
      <div className="flex justify-content-btwn training-header">
        <div>
          <h3 className="mb-0">Training Order History</h3>
        </div>
        <div>
          <Button
            className="grey-link-button"
            onClick={() => setShowExpiredOrders(!showExpiredOrders)}
          >
            {showExpiredOrders ? <Visibility /> : <VisibilityOff />}
            Show Expired Trainings
          </Button>
        </div>
      </div>
      {profileTrainingOrders.length > 0 ? (
        <TableContainer>
          <Table className="documents-table" style={{ marginTop: "10px" }}>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: "600px" }}>TRAINING</TableCell>
                <TableCell style={{ width: "200px" }}>LOCATION</TableCell>
                <TableCell style={{ width: "160px" }}>START DATE</TableCell>
                <TableCell style={{ width: "160px" }}>END DATE</TableCell>
                {canEditTrainings() && (
                  <TableCell style={{ width: "80px" }}></TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {profileTrainingOrders.map((order) => (
                <TableRow key={order.id}>
                  <TableCell>{order.training?.name}</TableCell>
                  <TableCell>{order.trainingLocation?.name}</TableCell>
                  <TableCell>
                    {order.startDate !== undefined && order.startDate !== ""
                      ? formatDateString(order.startDate!)
                      : ""}
                  </TableCell>
                  <TableCell>
                    {order.endDate !== undefined && order.endDate !== ""
                      ? formatDateString(order.endDate!)
                      : ""}
                  </TableCell>
                  {canEditTrainings() && (
                    <TableCell>
                      <Button
                        className="blue-link-button"
                        onClick={() => redirectToTrainingOrder(order.id!)}
                      >
                        <Edit />
                        Edit
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <p style={{ marginTop: "10px" }}>No Training Orders to Display</p>
      )}
    </>
  );
};

export default ProfileTrainings;
